import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useMutation } from "@apollo/client";
import Note3 from "components/Note/Note3";
import { ModernButton } from "components/ui/modernButton";
import { ComposeActions, ComposeHelpTrigger, ComposeSection } from "components/ui/v2";
import { useModal } from "context/modal/modal.provider";
import { gqlMessage } from "gql";
import { ICreateMessageInput } from "gql/Message/mutations";
import { CloudUpload, ImageIcon } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import Image from "next/legacy/image";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Popover, Uploader } from "rsuite";
import { FileType } from "rsuite/esm/Uploader/Uploader";
import { EMessageType, IMessage } from "types/Message.type";
import { IUser } from "types/User.types";
import useSound from "use-sound";
import { checkIfNodeHaveText } from "utils/message";

interface IForm {
  message: string;
  messageLength: number;
}

interface INewMagazineMessageProps {
  resetMessageInput: () => void;
  usersToMention: IUser[];
  inline?: boolean;
}

const MAX_THUMBNAIL_WIDTH = 960;
const MAX_THUMBNAIL_HEIGHT = 480;

const optimizeThumbnail = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new window.Image();
      img.onerror = () => reject(new Error("INVALID_IMAGE"));
      img.onload = () => {
        const ratio = Math.min(
          MAX_THUMBNAIL_WIDTH / img.width,
          MAX_THUMBNAIL_HEIGHT / img.height,
          1
        );
        const width = Math.max(1, Math.round(img.width * ratio));
        const height = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("CANVAS_NOT_AVAILABLE"));
          return;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.86));
      };
      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
  });

const NewMessageMagazine = (props: INewMagazineMessageProps) => {
  const { resetMessageInput, usersToMention, inline } = props;
  const [playComplete] = useSound("/sounds/feedback-sound.mp3", {
    volume: 0.25
  });
  const { closeModal } = useModal();
  const { control, handleSubmit, setValue, watch } = useForm<IForm>();
  const { t, lang } = useTranslation("common");
  const [contentEmpty, setContentEmpty] = useState<boolean>(true);
  const [thumbnail, setThumbnail] = useState<string>();

  const handleThumbnailChange = async (fileList: Array<FileType>) => {
    const file = fileList[fileList.length - 1]?.blobFile as File | undefined;
    if (!file) return;

    try {
      setThumbnail(await optimizeThumbnail(file));
    } catch {
      toast.error(t("No se pudo procesar la portada"));
    }
  };

  const [createMessage, { loading: createMessageLoading }] = useMutation<
    { createMessage: IMessage },
    {
      createMessageInput: ICreateMessageInput;
    }
  >(gqlMessage.mutations.CREATE_MESSAGE, {
    onCompleted: () => {
      setValue("message", "");
      resetMessageInput();
      toast.success(t("Mensaje publicado correctamente"));
      playComplete();
      closeModal();
    },
    onError: () => {
      toast.error(t("No se pudo publicar el Canva"));
    }
  });

  const handlePublish = async (data: IForm) => {
    if (createMessageLoading) return;

    await createMessage({
      variables: {
        createMessageInput: {
          type: EMessageType.MAGAZINE,
          text: data?.message,
          thumbnail
        }
      }
    });
  };

  useEffect(() => {
    const value = watch("message");
    if (value) {
      const blocks = JSON.parse(value);
      const hasText = checkIfNodeHaveText(blocks);

      // * check if at least one children has text
      if (hasText) {
        setContentEmpty(false);
      } else {
        setContentEmpty(true);
      }
    }
  }, [watch("message")]);

  const speaker = (
    <Popover title={t("✨ Copia y pega el embebido de Canva")}>
      <div className="flex flex-col items-start">
        <Image
          src={
            lang === "en"
              ? "https://cdn.ndrz.io/groowcity/general/how-to-embed-canva-en.gif"
              : "https://cdn.ndrz.io/groowcity/general/how-to-embed-canva.gif"
          }
          width={480}
          height={480}
          alt="how-to-embed-canvas"
          className="rounded-lg"
        />
      </div>
    </Popover>
  );

  if (inline) {
    return (
      <>
        <ComposeSection compact className="!px-3 !pb-2 !pt-2">
          <div className="mx-auto grid w-full max-w-[640px] grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/90 text-[10px] font-bold text-white">
                  1
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-600/80 dark:text-sky-400/80">
                  {t("Embed")}
                </span>
              </div>
              <div className="w-full min-h-[7.5rem]">
                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <Note3
                      {...field}
                      compact
                      embedComposer
                      showIAButton={false}
                      onChange={(e) => field.onChange(e)}
                      placeholder={t("Pega aquí el código <embed>…")}
                      usersToMention={usersToMention}
                      enableImageButton={false}
                      enableTextButtons={true}
                      enableLinkButton={true}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex w-full items-center justify-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/90 text-[10px] font-bold text-white">
                  2
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-600/80 dark:text-sky-400/80">
                  {t("Portada")}
                </span>
              </div>
              <Uploader
                accept="image/png, image/jpeg"
                multiple={false}
                fileListVisible={false}
                listType="picture"
                autoUpload={false}
                onChange={handleThumbnailChange}
                action=""
                classPrefix="border-none"
                className="magazine-cover-uploader block w-full min-h-[7.5rem] [&_.rs-uploader]:!block [&_.rs-uploader]:!w-full [&_.rs-uploader-file-items]:!hidden [&_.rs-uploader-trigger]:!float-none [&_.rs-uploader-trigger]:!block [&_.rs-uploader-trigger]:!w-full [&_.rs-uploader-trigger-btn]:!flex [&_.rs-uploader-trigger-btn]:!flex-col [&_.rs-uploader-trigger-btn]:!h-auto [&_.rs-uploader-trigger-btn]:!min-h-[7.5rem] [&_.rs-uploader-trigger-btn]:!w-full [&_.rs-uploader-trigger-btn]:!max-w-none [&_.rs-uploader-trigger-btn]:!cursor-pointer [&_.rs-uploader-trigger-btn]:!items-center [&_.rs-uploader-trigger-btn]:!justify-center [&_.rs-uploader-trigger-btn]:!border-0 [&_.rs-uploader-trigger-btn]:!bg-transparent [&_.rs-uploader-trigger-btn]:!p-0 [&_.rs-uploader-trigger-btn]:after:!hidden"
              >
                {thumbnail ? (
                  <div className="group relative h-full min-h-[7.5rem] w-full cursor-pointer overflow-hidden rounded-2xl border border-slate-200/80 dark:border-zinc-700/60">
                    <Image
                      src={thumbnail}
                      width={560}
                      height={140}
                      className="h-full min-h-[7.5rem] w-full bg-slate-100 object-contain dark:bg-zinc-900"
                      alt="thumbnail"
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-950/45 opacity-0 backdrop-blur-[2px] transition-opacity duration-200 group-hover:opacity-100">
                      <CloudUpload className="h-5 w-5 text-white" />
                      <span className="text-[11px] font-medium text-white">
                        {t("Cambiar imagen")}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[7.5rem] w-full cursor-pointer flex-col items-center justify-center gap-2.5 rounded-2xl border border-dashed border-sky-300/80 bg-sky-50/25 px-4 py-5 text-center transition-colors duration-200 hover:border-sky-400 hover:bg-sky-50/45 dark:border-sky-700/55 dark:bg-sky-950/15 dark:hover:border-sky-600/70 dark:hover:bg-sky-950/25">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-sky-600 shadow-[0_1px_3px_rgba(14,165,233,0.12)] ring-1 ring-sky-200/80 dark:bg-zinc-900 dark:text-sky-400 dark:ring-sky-800/60">
                      <ImageIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                    </span>
                    <span className="flex flex-col items-center gap-0.5">
                      <span className="text-[13px] font-semibold leading-tight text-slate-700 dark:text-zinc-200">
                        {t("Subir portada")}
                      </span>
                      <span className="text-[11px] leading-tight text-slate-400 dark:text-zinc-500">
                        PNG · JPG
                      </span>
                    </span>
                  </div>
                )}
              </Uploader>
            </div>
          </div>
        </ComposeSection>

        <ComposeActions
          compact
          onPublish={handleSubmit(handlePublish)}
          loading={createMessageLoading}
          disabled={contentEmpty}
          loadingText={t("Publicando...")}
          publishText={t("Publicar")}
          publishVariant="sky"
          secondary={
            <ComposeHelpTrigger
              label={t("¿Como funciona?")}
              speaker={speaker}
              accent="sky"
            />
          }
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Content Creation Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">
            {t("Crea tu contenido para revista")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate__animated animate__fadeIn relative">
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <Note3
                  {...field}
                  onChange={(e) => field.onChange(e)}
                  placeholder={t("Copia y pega el enlace <embed> desde Canva")}
                  usersToMention={usersToMention}
                  enableImageButton={false}
                  enableTextButtons={true}
                  enableLinkButton={true}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Cover Image Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">
            {t("Agrega una imagen de portada")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {thumbnail && (
              <div className="flex items-center justify-center">
                <div className="relative overflow-hidden rounded-lg shadow-md">
                  <Image
                    src={thumbnail}
                    width={200}
                    height={200}
                    className="rounded-md bg-slate-100 object-contain dark:bg-zinc-900"
                    alt="thumbnail"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col items-center space-y-3">
              <Uploader
                accept="image/png, image/jpeg"
                multiple={false}
                fileListVisible={false}
                listType="picture"
                autoUpload={false}
                onChange={handleThumbnailChange}
                action=""
                classPrefix="border-none"
              >
                <Button
                  variant="outline"
                  className="min-h-14 min-w-14 px-6 py-3 transition-all duration-300 hover:scale-110 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:shadow-lg active:scale-95 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30"
                >
                  <CloudUpload className="mr-2 h-5 w-5 transition-colors duration-300 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                  {t("Subir imagen")}
                </Button>
              </Uploader>

              <div className="space-y-1 text-center">
                <p className="font-medium text-blue-600 dark:text-blue-400">
                  {t("Click para subir un cover")} &nbsp;
                  <span className="text-muted-foreground">
                    {t("o arrastra y suelta")}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("Recuerda, puedes subir PNG, JPG or GIF (max. 960x480px)")}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Section */}
      <div className="flex items-center justify-between pt-4">
        <ComposeHelpTrigger
          label={t("¿Como funciona?")}
          speaker={speaker}
          placement="left"
          accent="sky"
        />

        <ModernButton
          variant="primary"
          onClick={handleSubmit(handlePublish)}
          loading={createMessageLoading}
          loadingText={t("Publicando...")}
          disabled={contentEmpty}
          className="h-12 min-w-[140px] px-8 py-3 text-base font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
        >
          {createMessageLoading ? (
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              <span>{t("Publicando...")}</span>
            </div>
          ) : (
            t("Publicar")
          )}
        </ModernButton>
      </div>
    </div>
  );
};

export default NewMessageMagazine;
